import React from 'react';

import { ISOBSERVER } from '../../constants/global';
import { useGlobalContext } from '../../context/global';
import { type IAgentChatPanelData } from '../../interface/peer-chat';
import { parentLevelLabel } from '../../lib/entity-code';
import { presenceLine } from '../../lib/peer-chat-copy';
import { peerChatEnabled } from '../../lib/peer-chat-flags';

/**
 * The Help surface behind the floating button.
 *
 * <p>One entry point for three things that used to be scattered: live chat, the feedback
 * form (previously the only thing this button did), and the agent's own report history.</p>
 *
 * <p>Chat is first because it is the one with a person on the other end.</p>
 */
const HelpHub = () => {
	const { rsProps, supportChat, chatUnread, agentHierarchy, peerInbox, states, supportSession } = useGlobalContext();
	const unread = supportChat?.unread ?? 0;
	const me = supportSession?.me ?? 0;
	const electionOfficial = states?._election?.get_ElectionOfficial?.data?.find((i) => i.userId === me);
	const resultType = electionOfficial?.assignment?.resultType?.toLowerCase();
	const electionId = electionOfficial?.electionId ?? 0;
	const isParent = !!resultType && resultType !== 'ec8a';
	const parentLevel = parentLevelLabel(resultType);
	const parent = agentHierarchy?.parents?.[0];

	/**
	 * The nearest human first: a parent's agents, or a child's own supervisor. The desk is the
	 * escalation and stays second, unchanged.
	 */
	const isObserver = ISOBSERVER();

	/**
	 * An observer's nearest person: their organisation's state observer, or — for the state observer —
	 * the observers they supervise. From api.core's observer hierarchy, never the party one.
	 */
	const observerRow = (() => {
		if (!isObserver || !peerChatEnabled() || agentHierarchy?.status !== 'ready') return null;
		const observerElection = agentHierarchy.electionId;
		if (agentHierarchy.children.length > 0) {
			return {
				key: 'observers',
				title: 'Your observers',
				subtitle: `${agentHierarchy.children.length} posted in your state`,
				badge: chatUnread?.peer ?? 0,
				onClick: () => rsProps?.callSection({ action: 'view', component: 'observer-peers', title: 'Your observers' }),
			};
		}
		const supervisor = agentHierarchy.parents[0];
		if (!supervisor) return null;
		return {
			key: 'supervisor',
			title: 'Your state observer',
			subtitle: supervisor.fullName,
			badge: peerInbox?.unreadByCounterpart.get(supervisor.userId) ?? 0,
			onClick: () => {
				const data: IAgentChatPanelData = {
					counterpart: {
						coreUserId: supervisor.userId,
						name: supervisor.fullName || 'State observer',
						entityCode: supervisor.entityCode,
						level: supervisor.level,
						role: 'parent',
						phone: supervisor.phoneNumber ?? undefined,
					},
					electionId: observerElection,
					origin: 'help-hub',
				};
				rsProps?.callSection({ action: 'view', component: 'observer-chat', title: data.counterpart.name, data });
			},
		};
	})();

	const peerRow = (() => {
		// Agent peer chat walks the party hierarchy; an observer has their own row above.
		if (isObserver || !peerChatEnabled() || !resultType || !electionId) return null;
		if (isParent) {
			const count = agentHierarchy?.children?.length ?? 0;
			const noun = resultType === 'ec8b' ? 'polling-unit agents' : resultType === 'ec8c' ? 'ward agents' : 'LGA agents';
			return {
				key: 'messages',
				title: 'Messages',
				subtitle: count > 0 ? `Your ${count} ${noun}` : `Your ${noun}`,
				badge: chatUnread?.peer ?? 0,
				onClick: () =>
					rsProps?.callSection({ action: 'view', component: 'messages-inbox', title: 'Messages', data: { electionId } }),
			};
		}
		if (!parentLevel) return null;
		const title = `Your ${parentLevel} agent`;
		if (!parent) {
			return {
				key: 'parent',
				title,
				subtitle: `No ${parentLevel} agent assigned yet`,
				badge: 0,
				onClick: () =>
					rsProps?.callSection({ action: 'view', component: 'messages-inbox', title: 'Messages', data: { electionId } }),
			};
		}
		const summary = peerInbox?.summaryFor(parent.userId);
		const presence = summary?.counterpartLastSeenAt
			? presenceLine({ lastSeenAt: summary.counterpartLastSeenAt, inThisThread: !!summary.counterpartInThreadKey }, false)
			: null;
		return {
			key: 'parent',
			title,
			subtitle: presence ? `${parent.fullName} · ${presence}` : parent.fullName,
			badge: peerInbox?.unreadByCounterpart.get(parent.userId) ?? 0,
			onClick: () => {
				const data: IAgentChatPanelData = {
					counterpart: {
						coreUserId: parent.userId,
						name: parent.fullName,
						entityCode: parent.entityCode,
						level: parent.level,
						role: 'parent',
						phone: parent.phoneNumber ?? undefined,
					},
					electionId,
					origin: 'help-hub',
				};
				rsProps?.callSection({ action: 'view', component: 'agent-chat', title: parent.fullName, data });
			},
		};
	})();

	const rows = [
		...(observerRow ? [observerRow] : []),
		...(peerRow ? [peerRow] : []),
		{
			key: 'chat',
			title: isObserver ? 'Chat with your observer desk' : 'Chat with support',
			// The server routes an observer's thread to their own organisation's desk, never a party's.
			subtitle: isObserver ? 'Your organisation’s coordinators reply here' : 'Get help from your party desk now',
			badge: unread,
			onClick: () =>
				rsProps?.callSection({ action: 'view', component: 'support-chat', title: isObserver ? 'Observer desk' : 'Support desk' }),
		},
		{
			key: 'report',
			title: 'Report a problem',
			subtitle: 'Send a bug or suggestion to the iElect team',
			badge: 0,
			onClick: () => rsProps?.callSection({ action: 'create', component: 'submit-feedback', title: 'Report a problem' }),
		},
		{
			key: 'mine',
			title: 'My reports',
			subtitle: 'See what you have sent and its status',
			badge: 0,
			onClick: () => rsProps?.callSection({ action: 'view', component: 'feedback-mine', title: 'My reports' }),
		},
	];

	return (
		<div className='help-hub'>
			{rows.map((row) => (
				<button
					key={row.key}
					type='button'
					className='help-hub__row'
					onClick={row.onClick}
				>
					<span className='help-hub__text'>
						<span className='help-hub__title'>{row.title}</span>
						<span className='help-hub__subtitle'>{row.subtitle}</span>
					</span>
					{row.badge > 0 ? (
						<span className='help-hub__badge'>
							{row.badge > 99 ? '99+' : row.badge}
							<span className='visually-hidden'> unread messages</span>
						</span>
					) : null}
					<span aria-hidden='true'>›</span>
				</button>
			))}
		</div>
	);
};

export default HelpHub;
